// Loads the signed-in user's permission codes from GET api/console/me into the auth store.
//
// Task 326.17 (decision Q27). The JWT no longer carries a `permissions` claim, so hasPermission()
// has nothing to check until this runs. Fetched once per session and once more after a tenant
// switch — keyed on `${userId}:${tenantId}`, which is what authStore.permissionsFor tracks. A
// silent token refresh does not refetch: same user, same tenant, same grants.
//
// Failure is deliberately silent: permissions stay empty, so the UI hides gated actions rather than
// offering ones the API would refuse. The BFF is the enforcement point either way.

import { useEffect } from 'react';
import { getMe } from '@/api/endpoints/me';
import { useAuthStore } from '@/stores/authStore';

export function useSessionPermissions(): void {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const userId = useAuthStore((s) => s.user?.userId ?? null);
  const tenantId = useAuthStore((s) => s.currentTenantId);
  const permissionsFor = useAuthStore((s) => s.permissionsFor);
  const setPermissions = useAuthStore((s) => s.setPermissions);

  const scope = userId ? `${userId}:${tenantId ?? ''}` : null;

  useEffect(() => {
    if (!isAuthenticated || !scope || permissionsFor === scope) return;

    let cancelled = false;

    getMe()
      .then((me) => {
        if (cancelled) return;
        // Guard against a late response for a tenant the user has already switched away from.
        const current = useAuthStore.getState();
        const currentScope = current.user?.userId
          ? `${current.user.userId}:${current.currentTenantId ?? ''}`
          : null;
        if (currentScope !== scope) return;

        setPermissions(me.permissions, scope);
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, scope, permissionsFor, setPermissions]);
}
